import React, { useEffect, useState, useCallback } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import axios from 'axios'
import { capitalize } from 'lodash'
import { Plus } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs'
import { Button } from '../ui/button'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import EmptyList from '../leevo_ui/EmptyList/EmptyList'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import ConfirmationDialog from '../leevo_ui/ConfirmationDialog/ConfirmationDialog'
import { requestCreator } from '../../utilities/requests'
import emptyCalendar from '../../images/empty-calendar.svg'
import useFacility from '../../utilities/hooks/useFacility'
import ClassPackagesTable from './ClassPackagesTable/ClassPackagesTable'
import ClassPackageEditDrawer from './ClassPackageEditDrawer/ClassPackageEditDrawer'
import toast from '../../utilities/toast'

const { get: getKlassPackages, cancel: cancelKlassPackagesRequests } = requestCreator()
const { put: updateKlassPackage } = requestCreator()
const { delete: deleteKlassPackage } = requestCreator()

function FacilitiesKlassPackagesIndex() {
  const { isLoading: isFacilityLoading } = useFacility()
  const { facility_id: facilityId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const [klassPackages, setKlassPackages] = useState({
    active: [],
    disabled: []
  })
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [editingPackageId, setEditingPackageId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [packageToDelete, setPackageToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [packageToToggle, setPackageToToggle] = useState(null)
  const [isToggling, setIsToggling] = useState(false)
  const [activeTab, setActiveTab] = useState('active')

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const editId = params.get('edit')
    if (editId) {
      setEditingPackageId(editId)
      setDrawerOpen(true)
    }
  }, [location.search])

  const loadKlassPackages = useCallback(async () => {
    setIsLoading(true)
    setLoadError(false)

    try {
      const packages = await getKlassPackages(`/api/facilities/${facilityId}/class_packages`)
      const records = packages || []

      setKlassPackages({
        active: records.filter((pkg) => pkg.active),
        disabled: records.filter((pkg) => !pkg.active)
      })
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoadError(true)
        toast.error('Failed to load class packages. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [facilityId])

  useEffect(() => {
    if (!isFacilityLoading) {
      loadKlassPackages()
    }

    return cancelKlassPackagesRequests
  }, [isFacilityLoading, loadKlassPackages])

  function handleEditClick(packageId) {
    setEditingPackageId(packageId)
    setDrawerOpen(true)
    history.push(`/facilities/${facilityId}/class_packages?edit=${packageId}`)
  }

  function handleDrawerClose(open) {
    setDrawerOpen(open)
    if (!open) {
      setEditingPackageId(null)
      history.push(`/facilities/${facilityId}/class_packages`)
    }
  }

  function handleSuccess(message) {
    toast.success(message)
    loadKlassPackages()
  }

  function handleDeleteClick(pkg) {
    setPackageToDelete(pkg)
  }

  async function handleDeleteConfirm() {
    setIsDeleting(true)
    try {
      await deleteKlassPackage(`/api/facilities/${facilityId}/class_packages/${packageToDelete.id}`)
      toast.success('Class package deleted successfully!')
      setPackageToDelete(null)
      loadKlassPackages()
    } catch (error) {
      toast.error('Failed to delete class package. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  function handleToggleClick(pkg) {
    setPackageToToggle(pkg)
  }

  async function handleToggleConfirm() {
    setIsToggling(true)
    try {
      const newActiveState = !packageToToggle.active
      await updateKlassPackage(`/api/facilities/${facilityId}/class_packages/${packageToToggle.id}`, {
        klassPackage: {
          active: newActiveState
        }
      })
      toast.success(`Class package ${newActiveState ? 'enabled' : 'disabled'} successfully!`)
      setPackageToToggle(null)
      loadKlassPackages()
    } catch (error) {
      toast.error('Failed to update class package. Please try again.')
    } finally {
      setIsToggling(false)
    }
  }

  return (
    <ApplicationLayout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">Class Packages</h1>
          <Button
            onClick={() => history.push(`/facilities/${facilityId}/class_packages/new`)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Class Package
          </Button>
        </div>
      </div>
      {isLoading ? (
        <LoadingSpinner size="1/5" className="flex items-center justify-center h-full" />
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <p className="text-gray-700 mb-4">Failed to load class packages. Please try again.</p>
          <Button variant="outline" onClick={loadKlassPackages}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col flex-1 bg-gray-100" data-cy="klass-package-list">
          <div className="w-full max-w-7xl mx-auto px-4 py-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex p-1 mb-3 space-x-1 shadow-inner bg-gray-200/50 rounded-xl w-60">
                <TabsTrigger value="active" className="flex-1">
                  {capitalize('active')}
                </TabsTrigger>
                <TabsTrigger value="disabled" className="flex-1">
                  {capitalize('disabled')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active">
                <div className="bg-white rounded-lg shadow">
                  {klassPackages.active.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <EmptyList
                        message="You have no active class packages set for your menu. Create some class packages to allow new students to register."
                        svgSrc={emptyCalendar}
                        buttonText="Create One +"
                        onButtonClick={() => history.push(`/facilities/${facilityId}/class_packages/new`)}
                      />
                    </div>
                  ) : (
                    <ClassPackagesTable
                      klassPackages={klassPackages.active}
                      onEditClick={handleEditClick}
                      onDeleteClick={handleDeleteClick}
                      onToggleClick={handleToggleClick}
                    />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="disabled">
                <div className="bg-white rounded-lg shadow">
                  {klassPackages.disabled.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <EmptyList
                        message="You have no disabled class packages set on your menu."
                        svgSrc={emptyCalendar}
                        buttonText="Create One +"
                        onButtonClick={() => history.push(`/facilities/${facilityId}/class_packages/new`)}
                      />
                    </div>
                  ) : (
                    <ClassPackagesTable
                      klassPackages={klassPackages.disabled}
                      onEditClick={handleEditClick}
                      onDeleteClick={handleDeleteClick}
                      onToggleClick={handleToggleClick}
                    />
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      <ClassPackageEditDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        klassPackageId={editingPackageId}
        facilityId={facilityId}
        onSuccess={handleSuccess}
      />

      <ConfirmationDialog
        isOpen={!!packageToDelete}
        onClose={() => setPackageToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Class Package?"
        message={
          packageToDelete
            ? `Deleting "${packageToDelete.title}" will permanently remove the package and its enrollments. Students may also be removed from upcoming classes connected to this package. This action cannot be undone.`
            : ''
        }
        confirmText="Delete Package"
        cancelText="Cancel"
        confirmColor="red"
        isLoading={isDeleting}
      />

      <ConfirmationDialog
        isOpen={!!packageToToggle}
        onClose={() => setPackageToToggle(null)}
        onConfirm={handleToggleConfirm}
        title={packageToToggle?.active ? 'Disable Class Package?' : 'Enable Class Package?'}
        message={
          packageToToggle
            ? packageToToggle.active
              ? `Disabling "${packageToToggle.title}" will remove it from your menu. New students will not be able to register for this package.`
              : `Enabling "${packageToToggle.title}" will make it available on your menu. New students will be able to register for this package.`
            : ''
        }
        confirmText={packageToToggle?.active ? 'Disable Package' : 'Enable Package'}
        cancelText="Cancel"
        confirmColor={packageToToggle?.active ? 'red' : 'purple'}
        isLoading={isToggling}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesKlassPackagesIndex
